import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import config from '../../../config/config';
import handleOpenSweetAlert from '../../handleOpenSweetAlert';
import { useSession } from '../../hooks/useSession';

export const useModalTesoreroController = (open, onClose, rowData, onSaved) => {
  const apiUrl = config.apiBaseUrl;
  const { loggedInUsername, idEmpresa } = useSession();

  const [paymentDetails, setPaymentDetails] = useState({
    metodoPago: '', proveedor: '', rfc: '', banco: '', cuenta: '', clabe: '',
    swift: '', observations: '', exhibit: '', fechaPago: '', fechaEnvioCarta: '',
    polizaErp: '', referencia: '',
  });

  const [files, setFiles] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [combinedErrors, setCombinedErrors] = useState({});
  const [referenciasArray, setReferenciasArray] = useState(['']);

  useEffect(() => {
    if (open && rowData) {
      setPaymentDetails({
        metodoPago: '', proveedor: '', rfc: '', banco: '', cuenta: '', clabe: '',
        swift: '', observations: '', exhibit: '', fechaPago: new Date().toISOString().split('T')[0], fechaEnvioCarta: '',
        polizaErp: '', referencia: '',
      });
      setFiles([]);
      setCombinedErrors({});
      setReferenciasArray(['']);
      fetchDatosProveedor(rowData.ID);
    }
  }, [open, rowData]);

  useEffect(() => {
    if (open && !metodosPago.length) {
      fetch(`${apiUrl}/api/metodospago`)
        .then(res => res.json())
        .then(data => {
          setMetodosPago(data || []);
        })
        .catch(err => console.error(err));
    }
  }, [open, metodosPago.length, apiUrl]);

  const fetchDatosProveedor = async (id) => {
    try {
      const res = await axios.get(`${apiUrl}/tesoreroDatosProveedor/${id}`);
      const d = res.data?.[0] || {};
      setPaymentDetails(p => ({
        ...p,
        proveedor: d.PROVEEDOR || '', rfc: d.RFC || '',
        banco: d.BANCO || '', cuenta: d.CUENTA || '', clabe: d.CLABE || '', swift: d.SWIFT || ''
      }));
    } catch (err) { }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setPaymentDetails(p => ({ ...p, [name]: value }));
    setCombinedErrors(p => { const u = { ...p }; delete u[name]; return u; });

    if (name === 'metodoPago') {
      const met = metodosPago.find(m => String(m.METODOPAGOID) === String(value));
      const label = (met?.METODOPAGO || '').toUpperCase();
      
      const isTransfer = label.includes('TRANSFERENCIA');
      const isOptional = label.includes('EFECTIVO') || label.includes('REFERENCIA') || label.includes('TARJETA');
      
      let reqRef = Number(met?.REQUIERE_REFERENCIA) === 1;
      let reqComp = Number(met?.REQUIERE_COMPROBANTE) === 1 || isTransfer || isOptional;

      if (!reqComp) { setFiles([]); setCombinedErrors(p => { const u = { ...p }; delete u.files; return u; }); }
      if (!reqRef) { setPaymentDetails(p => ({ ...p, referencia: '' })); setCombinedErrors(p => { const u = { ...p }; delete u.referencia; return u; }); }
    }
  }, [metodosPago, setPaymentDetails, setCombinedErrors, setFiles]);

  const handleReferenciaArrayChange = useCallback((index, value) => {
    setReferenciasArray(prev => {
      const nuevo = [...prev];
      nuevo[index] = value;
      // Sincronizar con paymentDetails.referencia
      setPaymentDetails(p => ({ ...p, referencia: nuevo.filter(x => x.trim()).join('; ') }));
      return nuevo;
    });
    setCombinedErrors(p => { const u = { ...p }; delete u.referencia; return u; });
  }, [setPaymentDetails, setCombinedErrors]);

  const addReferenciaField = useCallback(() => {
    setReferenciasArray(prev => [...prev, '']);
  }, []);

  const removeReferenciaField = useCallback((index) => {
    setReferenciasArray(prev => {
      const nuevo = prev.filter((_, i) => i !== index);
      const final = nuevo.length ? nuevo : [''];
      setPaymentDetails(p => ({ ...p, referencia: final.filter(x => x.trim()).join('; ') }));
      return final;
    });
  }, [setPaymentDetails]);

  const normalizeEmpresaArray = (id) => { if (Array.isArray(id)) return id; if (typeof id === 'string') return id.split(',').map(Number); return [Number(id)].filter(x => x); };

  const validate = () => {
    const errs = {};
    const met = metodosPago.find(m => String(m.METODOPAGOID) === String(paymentDetails.metodoPago));
    if (!met) {
        if (!paymentDetails.metodoPago) errs.metodoPago = 'Requerido';
        setCombinedErrors(errs);
        return false;
    }
    const label = (met?.METODOPAGO || '').toUpperCase();
    
    const isTransfer = label.includes('TRANSFERENCIA');
    const isOptional = label.includes('EFECTIVO') || label.includes('REFERENCIA') || label.includes('TARJETA');
    
    let reqRef = Number(met?.REQUIERE_REFERENCIA) === 1;
    let reqComp = Number(met?.REQUIERE_COMPROBANTE) === 1 || isTransfer || isOptional;
    const compMandatory = isTransfer;

    if (!paymentDetails.fechaPago) errs.fechaPago = 'Requerido';
    if (!paymentDetails.metodoPago) errs.metodoPago = 'Requerido';
    if (reqRef && !paymentDetails.referencia) errs.referencia = 'Requerido para este método';
    
    // Solo bloquea si es obligatorio (Transferencia) y no hay archivos
    if (reqComp && compMandatory && !files.length) {
        errs.files = 'El comprobante es obligatorio para transferencias bancarias';
    }

    setCombinedErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const autorizarPago = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(paymentDetails).forEach(([k, v]) => formData.append(k, v || ''));
      formData.append('AUT', 1);
      formData.append('MONTOSOLICITADO1', rowData.MONTOSOLICITADO || '');
      formData.append('FOLIO1', rowData.FOLIO || '');
      formData.append('CENTROCOSTO1', rowData.CENTROCOSTO || '');
      formData.append('DEPARTAMENTO1', rowData.DEPARTAMENTO || '');
      formData.append('SOLICITANTE1', rowData.SOLICITANTE || '');
      formData.append('ID1', rowData.ID);
      formData.append('nombreEmpresa', rowData.EMPRESA || '');
      formData.append('idEmpresa', JSON.stringify(normalizeEmpresaArray(idEmpresa)));
      formData.append('loggedInUsername', loggedInUsername || '');

      files.forEach(f => formData.append('adjunto', f));

      const res = await axios.put(`${apiUrl}/tesoreroProcesarPago`, formData);
      if (res.status >= 200 && res.status < 300) {
        handleOpenSweetAlert('Pago registrado con éxito', 'success');
        onClose();
        if (onSaved) onSaved();
      } else {
        handleOpenSweetAlert('Error al registrar el pago', 'error');
      }
    } catch (err) {
      handleOpenSweetAlert(err.response?.data?.error || 'Error al registrar pago', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedMet = metodosPago.find(m => String(m.METODOPAGOID) === String(paymentDetails.metodoPago));
  const selectedLabel = (selectedMet?.METODOPAGO || '').toUpperCase();

  return {
    paymentDetails, handleInputChange, files, setFiles, metodosPago,
    combinedErrors, loading, autorizarPago, setCombinedErrors,
    referenciasArray, handleReferenciaArrayChange, addReferenciaField, removeReferenciaField,
    compRules: {
      isTransfer: selectedLabel.includes('TRANSFERENCIA'),
      isOptional: selectedLabel.includes('EFECTIVO') || selectedLabel.includes('REFERENCIA') || selectedLabel.includes('TARJETA'),
      showSection: selectedLabel.includes('TRANSFERENCIA') || selectedLabel.includes('EFECTIVO') || selectedLabel.includes('REFERENCIA') || selectedLabel.includes('TARJETA') || Number(selectedMet?.REQUIERE_COMPROBANTE) === 1
    }
  };
};
